import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ListPumpPen } from '@cemex-core/cmx-api/models/calendars/calendar';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Store } from '@ngrx/store';
import { getFirstCalendar, getPumpCapacities } from '@ordersnproducts/modules/order-builder/reducers';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { distinctUntilChangedDeep } from '@ordersnproducts/modules/shared/utils/distinct-until-changed-deep';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  BOOM_PUMP,
  BOOM_PUMP_CODE,
  STATIONARY_PUMP,
  STATIONARY_PUMP_CODE,
} from '../../../rms-specification/constants/pumps';
import { SpecFieldNames } from '../../constants/specification-field-names.enum';
import { SpecificationFormState } from '../../reducers/specification/specification.reducer';
import { PUMP_SIZES } from '@ordersnproducts/modules/shared/constants/pumping';
import { TranslateService } from '@ngx-translate/core';

const pumpSizes = PUMP_SIZES;
const smallMin = 0;
const bigMin = 36;
/**
 * This service includes api pumps (Boom pump and Stationary pump)
 */
@Injectable()
/**
 * Service to load pump options based on the form group specification.
 * This service interacts with the store to fetch the necessary data and
 * provides options for Boom Pump and Stationary Pump based on the form group.
 *
 * @class PumpApiOptionLoaderUniversalLink
 * @constructor
 * @param {Store<SpecificationFormState>} store - The store containing the specification form state.
 * @param {FeatureToggleService} featureToggleService - The service to handle feature toggles.
 */
export class PumpApiOptionLoaderUniversalLink {
  constructor(
    protected store: Store<SpecificationFormState>,
    protected featureToggleService: FeatureToggleService,
    private translate: TranslateService,
  ) {}

  /**
   * Load options for the pump selection based on the form group.
   * This method checks the form group to determine if it indicates a Boom Pump or a Stationary Pump.
   * It then fetches the appropriate options from the store based on the pump type.
   * If neither pump type is indicated, it fetches the pump capacities from the store.
   *
   * @param formGroup The form group containing the specification fields.
   * @returns An observable of select options. Each select option contains an id and a name.
   */
  /**
   * Loads the available pump options based on the type of pump indicated by the provided form group.
   *
   * @param formGroup - The form group containing the pump type information.
   * @returns An observable that emits an array of select options.
   *
   * The method performs the following checks and operations:
   *
   * 1. **Boom Pump Check**:
   *    - If the form group indicates a Boom Pump, it retrieves options from the API response stored in the state.
   *    - Filters the calendar data to ensure it is available.
   *    - Maps the pump data to create an array of select options with `id` and `name` properties.
   *
   * 2. **Stationary Pump Check**:
   *    - If the form group indicates a Stationary Pump, it retrieves options from the API response stored in the state.
   *    - Filters the calendar data to ensure it is available and contains pump information.
   *    - Maps the pump data to create an array of select options with `id` and `name` properties.
   *
   * 3. **Default Pump Capacities**:
   *    - If neither Boom Pump nor Stationary Pump is indicated, it retrieves the default pump capacities from the state.
   *    - Filters the entries to ensure they are available.
   *    - Maps the entries to create an array of select options with `id`, `name`, and `default` properties.
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    // Check if the form group indicates a Boom Pump
    if (this.isBoomPump(formGroup)) {
      // Get options from API response (store)
      return this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar),
        take(1),
        map((calendar) => {
          const placingBoomPump =
            calendar && calendar.pump.unloadMethods.find((method) => method.unloadMethodCode === BOOM_PUMP_CODE);
          return (
            placingBoomPump &&
            placingBoomPump.listPumpPen.map((item) => {
              return {
                id: item.pumpSizeCode,
                name: this.makeDescription(item),
              };
            })
          );
        }),
      );
    }

    // Check if the form group indicates a Stationary Pump
    if (this.isStationaryPump(formGroup)) {
      // Get options from API response (store)
      return this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar && !!calendar.pump),
        take(1),
        map((calendar) => {
          const stationaryPump = calendar.pump.unloadMethods.find(
            (method) => method.unloadMethodCode === STATIONARY_PUMP_CODE,
          );
          return stationaryPump.listPumpStationary.map((item) => {
            return {
              id: item.pumpSizeCode,
              name: item.vehicleSize,
            };
          });
        }),
      );
    }

    return this.store.select(getPumpCapacities).pipe(
      distinctUntilChangedDeep(),
      filter((entries) => !!entries),
      take(1),
      map((entries) => {
        const options = entries.map((entry) => ({
          id: entry.pumpCapacityCode,
          name: entry.pumpCapacityDesc,
          default: false,
        }));
        return options;
      }),
    );
  }

  /**
   * Check if the form group indicates a Boom Pump.
   * @param formGroup The form group containing the specification fields.
   * @returns True if the unload type is Boom Pump, false otherwise.
   */
  isBoomPump(formGroup: UntypedFormGroup) {
    const field = formGroup.get(SpecFieldNames.UNLOAD_TYPE);
    return field && field.value === BOOM_PUMP;
  }

  /**
   * Check if the form group indicates a Stationary Pump.
   * @param formGroup The form group containing the specification fields.
   * @returns True if the unload type is Stationary Pump, false otherwise.
   */
  isStationaryPump(formGroup: UntypedFormGroup) {
    const field = formGroup.get(SpecFieldNames.UNLOAD_TYPE);
    return field && field.value === STATIONARY_PUMP;
  }

  /**
   * Create a description for the pump based on its size and capacity.
   * @param listPumpPen The pump pen list item.
   * @returns The description of the pump.
   */
  makeDescription(listPumpPen: ListPumpPen): string {
    const pumpCodes = [pumpSizes.anySizeCode, pumpSizes.specificSizeCode];
    // Show pump length just when is not specific size and any size
    if (pumpCodes.includes(listPumpPen.pumpSizeCode)) {
      return listPumpPen.vehicleSize;
    }
    if (listPumpPen.longMin === smallMin) {
      return `${listPumpPen.vehicleSize} (${listPumpPen.longMax} ${this.translate.instant('views.common.pump_size_unit')})`;
    } else if (listPumpPen.longMin === bigMin) {
      return `${listPumpPen.vehicleSize} (>= ${listPumpPen.longMin} ${this.translate.instant('views.common.pump_size_unit')})`;
    }
    /* tslint:disable:max-line-length */
    return `${listPumpPen.vehicleSize} (${listPumpPen.longMin} a ${listPumpPen.longMax} ${this.translate.instant('views.common.pump_size_unit')})`;
  }
}
